import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bike, Car, MapPin, Flag, Loader2, Plane, Building2, CalendarClock, ArrowLeft } from "lucide-react";
import AirportTransfer from "./AirportTransfer";
import { api, errMsg, fmt, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MapView, { RouteInfo } from "../map/MapView";
import PlaceSearch from "../map/PlaceSearch";
import { getRoute } from "../map/routing";
import useRideTracking from "../map/useRideTracking";
import { PageTitle, btnGhost, btnPrimary, inputCls } from "../components/ui";
import { RideDemoBanner, RideHistory, RideStatus, Stars, VEHICLE } from "../lib/ride";

const ACTIVE = ["requested", "accepted", "arriving", "in_progress"];
const STEPS = ["requested", "accepted", "arriving", "in_progress", "completed"];

function VehiclePicker({ value, onChange, fares }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[["moto", Bike], ["carro", Car]].map(([v, Icon]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`ride-vehicle-${v}`}
          className={`flex items-center gap-2 rounded-2xl border-2 p-3 text-left transition-colors ${value === v ? "border-bo-green bg-bo-green/5" : "border-gray-100 hover:border-gray-300"}`}>
          <Icon className="h-6 w-6 shrink-0 text-bo-green" />
          <span><strong className="block text-sm">{VEHICLE[v]}</strong><span className="text-xs text-gray-500">desde {fmt(fares?.[v]?.minimum)}</span></span>
        </button>
      ))}
    </div>
  );
}

function PointRow({ icon: Icon, label, point, active, onActivate, onPick, testid }) {
  return (
    <div className={`flex flex-col gap-1 rounded-2xl border-2 p-2 ${active ? "border-bo-green" : "border-gray-100"}`} data-testid={`${testid}-row`}>
      <button type="button" onClick={onActivate} className="flex items-center gap-2 text-left text-xs font-semibold" data-testid={`ride-mode-${testid}`}>
        <Icon className="h-4 w-4 text-bo-green" />{label}
        <span className="ml-auto font-normal text-gray-500" data-testid={`ride-${testid}-value`}>{point ? (point.label && point.label !== label ? point.label : `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`) : active ? "Toque no mapa ou pesquise" : "Por marcar"}</span>
      </button>
      {active && <PlaceSearch onPick={onPick} placeholder={`Pesquisar ${label.toLowerCase()}`} testid={`ride-search-${testid}`} />}
    </div>
  );
}

function RequestForm({ fares, onCreated }) {
  const [vehicle, setVehicle] = useState("moto");
  const [pickup, setPickup] = useState(null);
  const [dest, setDest] = useState(null);
  const [mode, setMode] = useState("pickup");
  const [route, setRoute] = useState(null);
  const [est, setEst] = useState(null);
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState(false);

  const setPoint = (p) => { if (mode === "pickup") { setPickup(p); setMode(dest ? null : "dest"); } else if (mode === "dest") { setDest(p); setMode(null); } };
  useEffect(() => {
    setRoute(null);
    if (!pickup || !dest) return;
    getRoute(pickup, dest).then((r) => {
      const off = (p, c) => Math.hypot(c[0] - p.lat, (c[1] - p.lng) * Math.cos(p.lat * Math.PI / 180)) * 111 > 0.6;
      if (r.road && (off(pickup, r.coords[0]) || off(dest, r.coords.at(-1)))) {
        toast.error("Um dos pontos não tem acesso por estrada. Escolha locais em terra, perto de uma estrada.");
        setDest(null); setMode("dest");
        return;
      }
      setRoute(r);
    });
  }, [pickup, dest]);
  const body = useCallback(() => ({ pickup: { lat: pickup.lat, lng: pickup.lng, label: pickup.label || "Origem" },
    destination: { lat: dest.lat, lng: dest.lng, label: dest.label || "Destino" }, vehicle,
    route_km: route?.road ? route.distanceKm : null, route_min: route?.road ? route.durationMin : null }), [pickup, dest, vehicle, route]);
  useEffect(() => {
    setEst(null);
    if (!pickup || !dest || !route) return;
    api.post("/ride-demo/estimate", body()).then(({ data }) => setEst(data)).catch((e) => toast.error(errMsg(e)));
  }, [route, vehicle]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ride-demo/trips", { ...body(), auto_driver: auto }); toast.success(`Pedido ${data.number} criado`); onCreated(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => { setPickup(null); setDest(null); setMode("pickup"); };
  return (
    <section className="bo-card flex flex-col gap-3 p-3 sm:p-5" data-testid="ride-request-form">
      <VehiclePicker value={vehicle} onChange={setVehicle} fares={fares} />
      <PointRow icon={MapPin} label="Origem" point={pickup} active={mode === "pickup"} onActivate={() => setMode("pickup")} onPick={setPoint} testid="pickup" />
      <PointRow icon={Flag} label="Destino" point={dest} active={mode === "dest"} onActivate={() => setMode("dest")} onPick={setPoint} testid="dest" />
      <MapView height={300} onClick={mode ? setPoint : undefined} route={route} testid="ride-map" fitSingle={false}
        markers={[{ key: "p", pos: pickup, icon: "me", label: "Origem" }, { key: "d", pos: dest, icon: "dest", label: "Destino" }]} />
      <p className="text-xs text-gray-500" data-testid="ride-points-status">
        {!pickup ? "1. Toque no mapa para marcar a origem." : !dest ? "2. Agora toque no mapa para marcar o destino." : "Origem e destino marcados."}
        {(pickup || dest) && <button type="button" onClick={reset} className="ml-2 underline" data-testid="ride-clear-points">Recomeçar</button>}
      </p>
      {!est && <RouteInfo route={route} />}
      {pickup && dest && !est && <p className="flex items-center gap-2 text-sm text-gray-500" data-testid="ride-estimate-loading"><Loader2 className="h-4 w-4 animate-spin" />A calcular percurso e tarifa...</p>}
      {est && (
        <div className="flex flex-col gap-3 rounded-2xl border-2 border-bo-green/30 bg-white p-4" data-testid="ride-estimate">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <strong className="text-2xl" data-testid="ride-estimate-price">{fmt(est.price)}</strong>
            <span className="text-sm text-gray-600">{VEHICLE[est.vehicle]} · {est.distance_km} km · ~{est.duration_min} min</span>
          </div>
          <span className="text-xs text-amber-800">Tarifa DEMO calculada no servidor · nada é cobrado</span>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="ride-auto-driver-checkbox" />
            Motorista DEMO simulado automático (desmarque para testar com o painel do motorista)</label>
          <button disabled={busy} onClick={request} className={`${btnPrimary} w-full disabled:opacity-50`} data-testid="ride-request-btn">{busy ? "A pedir..." : `Pedir ${VEHICLE[est.vehicle]} DEMO`}</button>
        </div>
      )}
    </section>
  );
}

function Progress({ status }) {
  const i = STEPS.indexOf(status);
  return (
    <div className="flex gap-1" data-testid="ride-progress">
      {STEPS.slice(1).map((s, k) => <span key={s} className={`h-1.5 flex-1 rounded-full ${k < i ? "bg-bo-green" : "bg-gray-200"}`} />)}
    </div>
  );
}

function ActiveTrip({ id, onChange }) {
  const { trip, mode, refresh } = useRideTracking(id);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(t); }, []);
  useEffect(() => { if (trip && !ACTIVE.includes(trip.status)) onChange(); }, [trip?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!trip) return <p className="bo-card p-5 text-sm text-gray-500">A carregar viagem...</p>;
  const cancel = async () => { try { await api.post(`/ride-demo/trips/${id}/cancel`); toast.success("Viagem cancelada"); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const callSim = async () => { try { await api.post(`/ride-demo/trips/${id}/simulate`); toast.info("A chamar motorista DEMO simulado..."); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="bo-card flex flex-col gap-3 p-3 sm:p-5" data-testid="ride-active-trip">
      <div className="flex flex-wrap items-center gap-2">
        <strong data-testid="ride-active-number">{trip.number}</strong><RideStatus status={trip.status} />
        <span className="ml-auto text-xs text-gray-500" data-testid="ride-tracking-mode">{mode === "live" ? "Em tempo real" : "Atualização periódica"}</span>
      </div>
      <Progress status={trip.status} />
      {trip.driver ? (
        <div className="flex items-center gap-3 rounded-2xl bg-bo-bg p-3" data-testid="ride-driver-info">
          {trip.driver.vehicle === "moto" ? <Bike className="h-8 w-8 text-bo-green" /> : <Car className="h-8 w-8 text-bo-green" />}
          <div className="text-sm"><strong className="block">{trip.driver.name}</strong>
            <span className="text-gray-600">{VEHICLE[trip.driver.vehicle]}{trip.driver.plate && ` · matrícula ${trip.driver.plate}`}</span>
            {trip.driver.rating_avg && <span className="ml-2"><Stars value={Math.round(trip.driver.rating_avg)} /></span>}</div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600" data-testid="ride-waiting-driver">
          <Loader2 className="h-4 w-4 animate-spin" />À procura de motorista DEMO...
          <button onClick={callSim} className={`${btnGhost} px-3 py-1 text-xs`} data-testid="ride-call-sim-btn">Chamar motorista simulado</button>
        </div>
      )}
      <MapView height={260} testid="ride-tracking-map" markers={[{ key: "p", pos: trip.pickup, icon: "me", label: "Origem" },
        { key: "d", pos: trip.destination, icon: "dest", label: "Destino" }, { key: "c", pos: trip.driver_location, icon: "courier", label: "Motorista DEMO" }]} />
      <p className="text-sm">Tarifa estimada: <strong>{fmt(trip.estimate.price)}</strong> <span className="text-xs text-amber-800">(DEMO, não cobrada)</span></p>
      <RideHistory trip={trip} />
      <div className="flex flex-wrap gap-2">
        {["requested", "accepted", "arriving"].includes(trip.status) && <button onClick={cancel} className={`${btnGhost} w-fit px-4 py-1.5`} data-testid="ride-cancel-btn">Cancelar viagem</button>}
        {trip.driver?.simulated && now - Date.parse(trip.location_at || trip.history.at(-1).at) > 15000 && <button onClick={callSim} className="text-xs text-gray-500 underline" data-testid="ride-resume-sim-btn">Simulação parada? Retomar</button>}
      </div>
    </section>
  );
}

function RateForm({ trip, onDone }) {
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const send = async () => { try { await api.post(`/ride-demo/trips/${trip.id}/rate`, { stars, comment }); toast.success("Obrigado pela avaliação"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`ride-rate-form-${trip.number}`}>
      <select value={stars} onChange={(e) => setStars(Number(e.target.value))} className={`${inputCls} w-auto`} data-testid="ride-rate-stars">{[5, 4, 3, 2, 1].map((s) => <option key={s} value={s}>{s} estrelas</option>)}</select>
      <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={300} placeholder="Comentário" className={`${inputCls} min-w-0 flex-1`} data-testid="ride-rate-comment" />
      <button onClick={send} className={`${btnPrimary} px-4 py-1.5`} data-testid="ride-rate-submit">Avaliar motorista</button>
    </div>
  );
}

function TripCard({ t, reload, highlight }) {
  const act = async (path, msg) => { try { await api.post(`/ride-demo/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={`bo-card flex flex-col gap-2 p-4 text-sm ${highlight ? "border-2 border-bo-green" : ""}`} data-testid={`ride-trip-${t.number}`}>
      {highlight && <p className="font-bold text-bo-green" data-testid="ride-completed-banner">Viagem DEMO concluída. Avalie o motorista.</p>}
      <div className="flex flex-wrap items-center gap-2"><strong>{t.number}</strong><RideStatus status={t.status} />
        <span className="text-xs text-gray-500">{VEHICLE[t.estimate.vehicle]} · {t.estimate.distance_km} km · {fmt(t.estimate.price)} · {fmtDate(t.created_at)}</span></div>
      {t.transfer && <p className="text-xs text-gray-600" data-testid="ride-trip-transfer">Transfer {t.transfer.direction === "arrival" ? "de chegada" : "de partida"} · {t.transfer.passengers} pass. · {t.transfer.luggage} malas
        {t.transfer.flight_number && ` · voo ${t.transfer.flight_number}`}{t.transfer.landing_at && ` · aterragem prevista ${fmtDate(t.transfer.landing_at)}`}</p>}
      <p className="text-xs text-gray-500">{t.pickup.label} → {t.destination.label}</p>
      {t.status === "scheduled" && <p className="text-xs font-semibold text-bo-green" data-testid="ride-trip-scheduled-at">Recolha agendada: {fmtDate(t.scheduled_at)} · reserva DEMO, disponibilidade não garantida</p>}
      {t.cancel_reason === "overlap" && <p className="text-xs font-semibold text-red-600" data-testid="ride-trip-overlap">Reserva não ativada: já tinha outra viagem em curso à hora marcada.</p>}
      {t.cancel_reason === "no_driver" && <p className="text-xs font-semibold text-red-600" data-testid="ride-trip-no-driver">Sem motorista DEMO disponível. Nada foi cobrado.</p>}
      {t.status === "scheduled" && <div className="flex flex-wrap gap-2">
        <button onClick={() => act(`trips/${t.id}/cancel`, "Reserva cancelada")} className={`${btnGhost} px-3 py-1 text-xs`} data-testid={`ride-scheduled-cancel-${t.number}`}>Cancelar reserva</button>
        <button onClick={() => act(`transfer/trips/${t.id}/simulate-time`, "Hora da reserva simulada: o servidor ativa o pedido em segundos")} className="text-xs text-gray-500 underline" data-testid={`ride-simulate-time-${t.number}`}>Simular hora da reserva (DEMO)</button>
      </div>}
      {t.driver && <p className="text-xs">Motorista: {t.driver.name}{t.driver.plate && ` · ${t.driver.plate}`}</p>}
      {t.rating && <p className="text-xs" data-testid="ride-trip-rating"><Stars value={t.rating.stars} /> {t.rating.comment}</p>}
      {t.status === "completed" && !t.rating && <RateForm trip={t} onDone={reload} />}
    </div>
  );
}

const SERVICES = [
  { id: "airport", title: "Transfer Aeroporto", text: "Chegada ou partida no Aeroporto Internacional de São Tomé", icon: Plane },
  { id: "city", title: "Viagem na cidade", text: "Mototáxi ou carro, agora", icon: Building2 },
  { id: "later", title: "Reserva antecipada", text: "Agende um transfer de aeroporto com antecedência", icon: CalendarClock },
];

function ServicePicker({ onPick }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="ride-services">
      {SERVICES.map(({ id, title, text, icon: Icon }) => (
        <button key={id} onClick={() => onPick(id)} className="bo-card flex flex-col gap-2 p-5 text-left transition-transform hover:-translate-y-0.5" data-testid={`ride-service-${id}`}>
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-bo-green text-bo-yellow"><Icon className="h-5 w-5" /></span>
          <strong>{title}</strong><span className="text-sm text-gray-600">{text}</span>
        </button>
      ))}
    </div>
  );
}

export default function RideDemo() {
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [status, setStatus] = useState(null);
  const [trips, setTrips] = useState([]);
  const load = useCallback(() => { if (user) api.get("/ride-demo/trips/mine").then(({ data }) => setTrips(data)); }, [user]);
  useEffect(() => { api.get("/ride-demo/status").then(({ data }) => setStatus(data)); load(); }, [load]);
  if (!status) return null;
  const active = trips.find((t) => ACTIVE.includes(t.status));
  const justDone = !active && trips[0]?.status === "completed" && !trips[0].rating ? trips[0] : null;
  const rest = trips.filter((t) => t !== active && t !== justDone);
  return (
    <div className="flex flex-col gap-4" data-testid="ride-demo-page">
      <PageTitle title="OBÔGO Mobilidade" sub="O teu destino. A tua viagem." />
      {!status.enabled ? (
        <p className="bo-card p-5 text-sm text-gray-600" data-testid="ride-demo-disabled">O transporte de passageiros está em preparação. Não existem viagens reais nesta fase.</p>
      ) : (
        <>
          <RideDemoBanner />
          {!user ? <p className="bo-card p-5 text-sm"><Link to="/entrar?next=/mobilidade" className="font-semibold text-bo-green underline" data-testid="ride-login-link">Entre na sua conta</Link> para experimentar o pedido de viagem DEMO.</p>
            : active ? <ActiveTrip key={active.id} id={active.id} onChange={load} /> : (
              <>{justDone && <TripCard t={justDone} reload={load} highlight />}
                {!service ? <ServicePicker onPick={setService} /> : (
                  <>
                    <button onClick={() => setService(null)} className="inline-flex w-fit items-center gap-1 text-sm text-gray-600" data-testid="ride-back-services"><ArrowLeft className="h-4 w-4" />Serviços</button>
                    {service === "city" ? <RequestForm fares={status.fares} onCreated={load} /> : <AirportTransfer key={service} defaultWhen={service === "later" ? "scheduled" : "now"} onCreated={() => { setService(null); load(); }} />}
                  </>
                )}</>
            )}
        </>
      )}
      {user && <section className="flex flex-col gap-2" data-testid="ride-trip-list"><h2 className="font-bold">As minhas viagens DEMO</h2>
        {!rest.length ? <p className="text-sm text-gray-500" data-testid="ride-history-empty">Sem outras viagens DEMO.</p> : rest.map((t) => <TripCard key={t.id} t={t} reload={load} />)}</section>}
    </div>
  );
}
